import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Play, Square, BarChart3, Phone, Hash, Tag, FileText, RefreshCw, Server, Wrench, Network, X, CheckCircle, XCircle } from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };
const inputClass = "w-full h-9 px-3 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors";
const inputBorder = { border: '1px solid rgba(99, 102, 241, 0.15)' };
const labelClass = "block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5";

const AdvancedRoutingManagement = () => {
  const [activeTab, setActiveTab] = useState('routes');
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  
  // Route Evaluation State
  const [testData, setTestData] = useState({
    entity_id: '',
    plan_id: '',
    channel: 'HTTP',
    mobile_number: '',
    header: '',
    template_id: '',
    message_content: ''
  });
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  
  // Number Series State
  const [numberSeries, setNumberSeries] = useState([]);
  const [showNumberSeriesModal, setShowNumberSeriesModal] = useState(false);
  const [editingNumberSeries, setEditingNumberSeries] = useState(null);
  const [numberSeriesForm, setNumberSeriesForm] = useState({
    prefix_start: '',
    prefix_end: '',
    route_id: '',
    priority: 1,
    is_active: true
  });
  
  // Mobile Numbers State
  const [mobileNumbers, setMobileNumbers] = useState([]);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [editingMobile, setEditingMobile] = useState(null);
  const [mobileForm, setMobileForm] = useState({
    mobile_number: '',
    action: 'ROUTE',
    route_id: '',
    priority: 1,
    is_active: true
  });
  
  // Headers State
  const [headers, setHeaders] = useState([]);
  const [showHeaderModal, setShowHeaderModal] = useState(false);
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerForm, setHeaderForm] = useState({
    header_name: '',
    route_id: '',
    priority: 1,
    is_active: true
  });
  
  // Keywords State
  const [keywords, setKeywords] = useState([]);
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [keywordForm, setKeywordForm] = useState({
    keyword: '',
    route_id: '',
    priority: 1,
    is_active: true
  });
  
  // Replacements State
  const [replacements, setReplacements] = useState([]);
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [editingReplacement, setEditingReplacement] = useState(null);
  const [replacementForm, setReplacementForm] = useState({
    find_text: '',
    replace_text: '',
    scope: 'GLOBAL',
    entity_id: '',
    plan_id: '',
    is_active: true
  });
  const [entitySearchTerm, setEntitySearchTerm] = useState('');
  const [planSearchTerm, setPlanSearchTerm] = useState('');
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const [showPlanDropdown, setShowPlanDropdown] = useState(false);
  const entityDropdownRef = useRef(null);
  const planDropdownRef = useRef(null);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (entityDropdownRef.current && !entityDropdownRef.current.contains(event.target)) {
        setShowEntityDropdown(false);
      }
      if (planDropdownRef.current && !planDropdownRef.current.contains(event.target)) {
        setShowPlanDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Routing Plans State
  const [routingPlans, setRoutingPlans] = useState([]);
  const [showRoutingPlanModal, setShowRoutingPlanModal] = useState(false);
  const [editingRoutingPlan, setEditingRoutingPlan] = useState(null);
  const [routingPlanForm, setRoutingPlanForm] = useState({
    plan_name: '',
    description: '',
    priority: 1,
    is_active: true
  });
  
  // Common state
  const [routes, setRoutes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [routeForm, setRouteForm] = useState({
    route_name: '',
    operator_name: '',
    endpoint_url: '',
    priority: 1,
    is_active: true,
    route_type: 'HTTP',
    authentication_type: 'NONE',
    username: '',
    password: '',
    api_key: '',
    timeout: 30,
    retry_count: 3,
    retry_delay: 5,
    weight: 100,
    description: '',
    headers: {},
    params: {}
  });

  useEffect(() => {
    fetchStatistics();
    fetchRoutes();
    fetchEntities();
    fetchPlans();
    fetchRoutingPlans();
    
    // Load data based on active tab
    if (activeTab === 'number-series') fetchNumberSeries();
    if (activeTab === 'mobile-numbers') fetchMobileNumbers();
    if (activeTab === 'headers') fetchHeaders();
    if (activeTab === 'keywords') fetchKeywords();
    if (activeTab === 'replacements') fetchReplacements();
    if (activeTab === 'routing-plans') fetchRoutingPlans();
  }, [activeTab]);

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/admin/advanced-routing/statistics');
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await api.get('/admin/http-routes');
      setRoutes(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    }
  };

  const fetchEntities = async () => {
    try {
      const response = await api.get('/admin/entities');
      setEntities(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch entities:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/admin/plans');
      setPlans(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchRoutingPlans = async () => {
    try {
      const response = await api.get('/admin/routing-plans');
      setRoutingPlans(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch routing plans:', error);
    }
  };

  const fetchNumberSeries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/advanced-routing/number-series');
      setNumberSeries(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch number series:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMobileNumbers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/advanced-routing/mobile-numbers');
      setMobileNumbers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch mobile numbers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeaders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/advanced-routing/headers');
      setHeaders(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch headers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchKeywords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/advanced-routing/keywords');
      setKeywords(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch keywords:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReplacements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/advanced-routing/keyword-replacements');
      setReplacements(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch replacements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluateRouting = async () => {
    try {
      setEvaluating(true);
      const response = await api.post('/admin/advanced-routing/evaluate', testData);
      setEvaluationResult(response.data.data);
    } catch (error) {
      console.error('Failed to evaluate routing:', error);
      alert('Failed to evaluate routing. Please check your inputs.');
    } finally {
      setEvaluating(false);
    }
  };

  const handleSaveNumberSeries = async () => {
    try {
      if (editingNumberSeries) {
        await api.put(`/admin/advanced-routing/number-series/${editingNumberSeries.id}`, numberSeriesForm);
      } else {
        await api.post('/admin/advanced-routing/number-series', numberSeriesForm);
      }
      setShowNumberSeriesModal(false);
      setEditingNumberSeries(null);
      setNumberSeriesForm({ prefix_start: '', prefix_end: '', route_id: '', priority: 1, is_active: true });
      fetchNumberSeries();
    } catch (error) {
      console.error('Failed to save number series:', error);
      alert('Failed to save number series');
    }
  };

  const handleSaveMobile = async () => {
    try {
      if (editingMobile) {
        await api.put(`/admin/advanced-routing/mobile-numbers/${editingMobile.id}`, mobileForm);
      } else {
        await api.post('/admin/advanced-routing/mobile-numbers', mobileForm);
      }
      setShowMobileModal(false);
      setEditingMobile(null);
      setMobileForm({ mobile_number: '', action: 'ROUTE', route_id: '', priority: 1, is_active: true });
      fetchMobileNumbers();
    } catch (error) {
      console.error('Failed to save mobile number:', error);
      alert('Failed to save mobile number');
    }
  };

  const handleSaveHeader = async () => {
    try {
      if (editingHeader) {
        await api.put(`/admin/advanced-routing/headers/${editingHeader.id}`, headerForm);
      } else {
        await api.post('/admin/advanced-routing/headers', headerForm);
      }
      setShowHeaderModal(false);
      setEditingHeader(null);
      setHeaderForm({ header_name: '', route_id: '', priority: 1, is_active: true });
      fetchHeaders();
    } catch (error) {
      console.error('Failed to save header:', error);
      alert('Failed to save header');
    }
  };

  const handleSaveKeyword = async () => {
    try {
      if (editingKeyword) {
        await api.put(`/admin/advanced-routing/keywords/${editingKeyword.id}`, keywordForm);
      } else {
        await api.post('/admin/advanced-routing/keywords', keywordForm);
      }
      setShowKeywordModal(false);
      setEditingKeyword(null);
      setKeywordForm({ keyword: '', route_id: '', priority: 1, is_active: true });
      fetchKeywords();
    } catch (error) {
      console.error('Failed to save keyword:', error);
      alert('Failed to save keyword');
    }
  };

  const handleSaveReplacement = async () => {
    try {
      if (editingReplacement) {
        await api.put(`/admin/advanced-routing/keyword-replacements/${editingReplacement.id}`, replacementForm);
      } else {
        await api.post('/admin/advanced-routing/keyword-replacements', replacementForm);
      }
      setShowReplacementModal(false);
      setEditingReplacement(null);
      setReplacementForm({ find_text: '', replace_text: '', scope: 'GLOBAL', entity_id: '', plan_id: '', is_active: true });
      setEntitySearchTerm('');
      setPlanSearchTerm('');
      setShowEntityDropdown(false);
      setShowPlanDropdown(false);
      fetchReplacements();
    } catch (error) {
      console.error('Failed to save replacement:', error);
      alert('Failed to save replacement rule');
    }
  };

  const handleSaveRoutingPlan = async () => {
    try {
      if (editingRoutingPlan) {
        await api.put(`/admin/routing-plans/${editingRoutingPlan.id}`, routingPlanForm);
      } else {
        await api.post('/admin/routing-plans', routingPlanForm);
      }
      setShowRoutingPlanModal(false);
      setEditingRoutingPlan(null);
      setRoutingPlanForm({ plan_name: '', description: '', priority: 1, is_active: true });
      fetchRoutingPlans();
    } catch (error) {
      console.error('Failed to save routing plan:', error);
      alert('Failed to save routing plan');
    }
  };

  const handleDeleteRoutingPlan = async (id) => {
    if (!window.confirm('Are you sure you want to delete this routing plan?')) return;
    
    try {
      await api.delete(`/admin/routing-plans/${id}`);
      fetchRoutingPlans();
    } catch (error) {
      console.error('Failed to delete routing plan:', error);
      alert('Failed to delete routing plan');
    }
  };

  const handleTestRoutingPlan = async (planId) => {
    try {
      const response = await api.post(`/admin/routing-plans/${planId}/test`);
      alert(`Test completed successfully!\n\nResult: ${response.data.message}`);
    } catch (error) {
      console.error('Failed to test routing plan:', error);
      alert('Failed to test routing plan');
    }
  };

  const handleToggleStatus = async (table, id) => {
    try {
      if (table === 'routing-plans') {
        await api.put(`/admin/routing-plans/${id}/status`);
        fetchRoutingPlans();
      } else {
        await api.put(`/admin/advanced-routing/${table}/${id}/status`);
        // Refresh the current tab data
        if (activeTab === 'number-series') fetchNumberSeries();
        if (activeTab === 'mobile-numbers') fetchMobileNumbers();
        if (activeTab === 'headers') fetchHeaders();
        if (activeTab === 'keywords') fetchKeywords();
        if (activeTab === 'replacements') fetchReplacements();
      }
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  const handleDelete = async (table, id) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await api.delete(`/admin/advanced-routing/${table}/${id}`);
      // Refresh the current tab data
      if (activeTab === 'number-series') fetchNumberSeries();
      if (activeTab === 'mobile-numbers') fetchMobileNumbers();
      if (activeTab === 'headers') fetchHeaders();
      if (activeTab === 'keywords') fetchKeywords();
      if (activeTab === 'replacements') fetchReplacements();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const renderStatCards = () => {
    if (!statistics) return null;
    const stats = [
      { label: 'Total Routes', value: statistics.total_routes || 0, color: '#4f46e5', bg: 'rgba(99, 102, 241, 0.06)', borderColor: 'rgba(99, 102, 241, 0.12)' },
      { label: 'Active Routes', value: statistics.active_routes || 0, color: '#059669', bg: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.12)' },
      { label: 'Number Series', value: statistics.number_series_count || 0, color: '#d97706', bg: 'rgba(245, 158, 11, 0.06)', borderColor: 'rgba(245, 158, 11, 0.12)' },
      { label: 'Mobile Numbers', value: statistics.mobile_numbers_count || 0, color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.06)', borderColor: 'rgba(124, 58, 237, 0.12)' },
    ];
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {stats.map((s, i) => (
          <div key={i} className="bg-white rounded-xl p-4" style={{ ...cardStyle, background: s.bg, borderColor: s.borderColor }}>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{s.label}</p>
            <span className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderRoutes = () => (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-bold text-gray-800">Routes</h3>
        <button
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md"
          style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
          onClick={() => setShowRouteModal(true)}
        >
          <Plus className="w-3.5 h-3.5" />
          Add Route
        </button>
      </div>
      
      <div className="overflow-x-auto rounded-xl" style={cardStyle}>
        <table className="min-w-full">
          <thead>
            <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Route Name</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Operator</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Priority</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Weight</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-6 py-12 text-center"><Server className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400">Loading...</p></td></tr>
            ) : routes.length === 0 ? (
              <tr><td colSpan="7" className="px-6 py-12 text-center"><Server className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No routes found</p></td></tr>
            ) : (
              routes.map((route) => (
                <tr key={route.id} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                  <td className="px-4 py-3">
                    <div className="text-xs font-semibold text-gray-800">{route.route_name}</div>
                    {route.description && <div className="text-[10px] text-gray-400 mt-0.5">{route.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{route.operator_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                      {route.route_type || 'HTTP'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{route.priority}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{route.weight || 100}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={route.is_active ? { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' } : { background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                      {route.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-0.5">
                      <button
                        className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50"
                        title="Edit"
                        onClick={() => {
                          setEditingRoute(route);
                          setRouteForm({
                            route_name: route.route_name || '',
                            operator_name: route.operator_name || '',
                            endpoint_url: route.endpoint_url || '',
                            priority: route.priority || 1,
                            is_active: route.is_active !== undefined ? route.is_active : true,
                            route_type: route.route_type || 'HTTP',
                            authentication_type: route.authentication_type || 'NONE',
                            username: route.username || '',
                            password: route.password || '',
                            api_key: route.api_key || '',
                            timeout: route.timeout || 30,
                            retry_count: route.retry_count || 3,
                            retry_delay: route.retry_delay || 5,
                            weight: route.weight || 100,
                            description: route.description || '',
                            headers: route.headers || {},
                            params: route.params || {}
                          });
                          setShowRouteModal(true);
                        }}
                      >
                        <Pencil className="w-3.5 h-3.5" style={{ color: '#6366f1' }} />
                      </button>
                      <button
                        className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50"
                        title="Delete"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this route?')) {
                            api.delete(`/admin/http-routes/${route.id}`)
                              .then(() => fetchRoutes())
                              .catch(error => console.error('Failed to delete route:', error));
                          }
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Route Modal */}
      {showRouteModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl mb-8" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900">
                {editingRoute ? 'Edit Route' : 'Add New Route'}
              </h3>
              <button onClick={() => { setShowRouteModal(false); setEditingRoute(null); }} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6">
              <form onSubmit={(e) => {
                e.preventDefault();
                const submitData = { ...routeForm };
                if (editingRoute) {
                  api.put(`/admin/http-routes/${editingRoute.id}`, submitData)
                    .then(() => {
                      fetchRoutes();
                      setShowRouteModal(false);
                      setEditingRoute(null);
                      setRouteForm({
                        route_name: '',
                        operator_name: '',
                        endpoint_url: '',
                        priority: 1,
                        is_active: true,
                        route_type: 'HTTP',
                        authentication_type: 'NONE',
                        username: '',
                        password: '',
                        api_key: '',
                        timeout: 30,
                        retry_count: 3,
                        retry_delay: 5,
                        weight: 100,
                        description: '',
                        headers: {},
                        params: {}
                      });
                    })
                    .catch(error => {
                      console.error('Failed to update route:', error);
                    });
                } else {
                  api.post('/admin/http-routes', submitData)
                    .then(() => {
                      fetchRoutes();
                      setShowRouteModal(false);
                      setRouteForm({
                        route_name: '',
                        operator_name: '',
                        endpoint_url: '',
                        priority: 1,
                        is_active: true,
                        route_type: 'HTTP',
                        authentication_type: 'NONE',
                        username: '',
                        password: '',
                        api_key: '',
                        timeout: 30,
                        retry_count: 3,
                        retry_delay: 5,
                        weight: 100,
                        description: '',
                        headers: {},
                        params: {}
                      });
                    })
                    .catch(error => {
                      console.error('Failed to create route:', error);
                    });
                }
              }}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Route Name *</label>
                      <input type="text" className={inputClass} style={inputBorder} value={routeForm.route_name} onChange={(e) => setRouteForm({ ...routeForm, route_name: e.target.value })} required />
                    </div>
                    <div>
                      <label className={labelClass}>Operator Name *</label>
                      <input type="text" className={inputClass} style={inputBorder} value={routeForm.operator_name} onChange={(e) => setRouteForm({ ...routeForm, operator_name: e.target.value })} required />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Description</label>
                    <textarea className={`${inputClass} h-auto py-2`} style={inputBorder} rows={2} value={routeForm.description} onChange={(e) => setRouteForm({ ...routeForm, description: e.target.value })} placeholder="Optional description" />
                  </div>

                  <div>
                    <label className={labelClass}>Endpoint URL *</label>
                    <input type="url" className={inputClass} style={inputBorder} value={routeForm.endpoint_url} onChange={(e) => setRouteForm({ ...routeForm, endpoint_url: e.target.value })} placeholder="https://example.com/api/sms" required />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Route Type</label>
                      <select className={inputClass} style={inputBorder} value={routeForm.route_type} onChange={(e) => setRouteForm({ ...routeForm, route_type: e.target.value })}>
                        <option value="HTTP">HTTP</option>
                        <option value="HTTPS">HTTPS</option>
                        <option value="SMPP">SMPP</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Authentication Type</label>
                      <select className={inputClass} style={inputBorder} value={routeForm.authentication_type} onChange={(e) => setRouteForm({ ...routeForm, authentication_type: e.target.value })}>
                        <option value="NONE">None</option>
                        <option value="BASIC">Basic Auth</option>
                        <option value="API_KEY">API Key</option>
                        <option value="BEARER">Bearer Token</option>
                      </select>
                    </div>
                  </div>

                  {routeForm.authentication_type === 'BASIC' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Username</label>
                        <input type="text" className={inputClass} style={inputBorder} value={routeForm.username} onChange={(e) => setRouteForm({ ...routeForm, username: e.target.value })} />
                      </div>
                      <div>
                        <label className={labelClass}>Password</label>
                        <input type="password" className={inputClass} style={inputBorder} value={routeForm.password} onChange={(e) => setRouteForm({ ...routeForm, password: e.target.value })} />
                      </div>
                    </div>
                  )}

                  {(routeForm.authentication_type === 'API_KEY' || routeForm.authentication_type === 'BEARER') && (
                    <div>
                      <label className={labelClass}>{routeForm.authentication_type === 'API_KEY' ? 'API Key' : 'Bearer Token'}</label>
                      <input type="text" className={inputClass} style={inputBorder} value={routeForm.api_key} onChange={(e) => setRouteForm({ ...routeForm, api_key: e.target.value })} placeholder={routeForm.authentication_type === 'API_KEY' ? 'Enter API key' : 'Enter bearer token'} />
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Priority *</label>
                      <input type="number" className={inputClass} style={inputBorder} value={routeForm.priority} onChange={(e) => setRouteForm({ ...routeForm, priority: parseInt(e.target.value) })} min="1" required />
                    </div>
                    <div>
                      <label className={labelClass}>Weight</label>
                      <input type="number" className={inputClass} style={inputBorder} value={routeForm.weight} onChange={(e) => setRouteForm({ ...routeForm, weight: parseInt(e.target.value) })} min="1" max="100" />
                    </div>
                    <div>
                      <label className={labelClass}>Timeout (sec)</label>
                      <input type="number" className={inputClass} style={inputBorder} value={routeForm.timeout} onChange={(e) => setRouteForm({ ...routeForm, timeout: parseInt(e.target.value) })} min="1" max="300" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Retry Count</label>
                      <input type="number" className={inputClass} style={inputBorder} value={routeForm.retry_count} onChange={(e) => setRouteForm({ ...routeForm, retry_count: parseInt(e.target.value) })} min="0" max="10" />
                    </div>
                    <div>
                      <label className={labelClass}>Retry Delay (sec)</label>
                      <input type="number" className={inputClass} style={inputBorder} value={routeForm.retry_delay} onChange={(e) => setRouteForm({ ...routeForm, retry_delay: parseInt(e.target.value) })} min="1" max="60" />
                    </div>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={routeForm.is_active} onChange={(e) => setRouteForm({ ...routeForm, is_active: e.target.checked })} />
                    <span className="text-xs font-medium text-gray-700">Active</span>
                  </label>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <button type="button" className="px-4 py-2 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-50" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }} onClick={() => { setShowRouteModal(false); setEditingRoute(null); setRouteForm({ route_name: '', operator_name: '', endpoint_url: '', priority: 1, is_active: true, route_type: 'HTTP', authentication_type: 'NONE', username: '', password: '', api_key: '', timeout: 30, retry_count: 3, retry_delay: 5, weight: 100, description: '', headers: {}, params: {} }); }}>Cancel</button>
                  <button type="submit" className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>{editingRoute ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderRouteEvaluation = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-5" style={cardStyle}>
        <h3 className="text-sm font-bold text-gray-800 mb-4">Test Routing Decision</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={labelClass}>Entity</label><select className={inputClass} style={inputBorder} value={testData.entity_id} onChange={(e) => setTestData({ ...testData, entity_id: e.target.value })}><option value="">Select Entity</option>{entities.map(e => <option key={e.id} value={e.id}>{e.entity_name}</option>)}</select></div>
          <div><label className={labelClass}>Plan</label><select className={inputClass} style={inputBorder} value={testData.plan_id} onChange={(e) => setTestData({ ...testData, plan_id: e.target.value })}><option value="">Select Plan</option>{plans.map(p => <option key={p.id} value={p.id}>{p.plan_name}</option>)}</select></div>
          <div><label className={labelClass}>Channel</label><select className={inputClass} style={inputBorder} value={testData.channel} onChange={(e) => setTestData({ ...testData, channel: e.target.value })}><option value="HTTP">HTTP</option><option value="SMPP">SMPP</option><option value="GUI">GUI</option><option value="API">API</option></select></div>
          <div><label className={labelClass}>Mobile Number</label><input type="text" className={inputClass} style={inputBorder} value={testData.mobile_number} onChange={(e) => setTestData({ ...testData, mobile_number: e.target.value })} placeholder="919876543210" /></div>
          <div><label className={labelClass}>Header</label><input type="text" className={inputClass} style={inputBorder} value={testData.header} onChange={(e) => setTestData({ ...testData, header: e.target.value })} placeholder="WAY2OTP" /></div>
          <div><label className={labelClass}>Template ID</label><input type="text" className={inputClass} style={inputBorder} value={testData.template_id} onChange={(e) => setTestData({ ...testData, template_id: e.target.value })} placeholder="TPL001" /></div>
          <div className="md:col-span-2"><label className={labelClass}>Message Content</label><textarea className={`${inputClass} h-auto py-2`} style={inputBorder} rows={3} value={testData.message_content} onChange={(e) => setTestData({ ...testData, message_content: e.target.value })} placeholder="Your OTP is 1234" /></div>
        </div>
        <div className="mt-4">
          <button className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }} onClick={handleEvaluateRouting} disabled={evaluating}>
            <Play className="w-3.5 h-3.5" /> {evaluating ? 'Evaluating...' : 'Evaluate Routing'}
          </button>
        </div>
      </div>
      {evaluationResult && (
        <div className="bg-white rounded-xl p-5" style={cardStyle}>
          <h3 className="text-sm font-bold text-gray-800 mb-3">Evaluation Result</h3>
          <div className="rounded-lg p-4" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{JSON.stringify(evaluationResult, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );

  const getStatusBadgeStyle = (active) => active ? { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' } : { background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)' };

  const renderActionButtons = (item, table, onEdit) => (
    <div className="flex items-center gap-0.5">
      <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50" title="Edit" onClick={onEdit}><Pencil className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /></button>
      <button className={`w-7 h-7 rounded-lg flex items-center justify-center ${item.is_active ? 'hover:bg-amber-50' : 'hover:bg-green-50'}`} title="Toggle" onClick={() => handleToggleStatus(table, item.id)}>{item.is_active ? <Square className="w-3.5 h-3.5 text-amber-500" /> : <Play className="w-3.5 h-3.5 text-green-500" />}</button>
      <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50" title="Delete" onClick={() => handleDelete(table, item.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
    </div>
  );

  const renderModalWrapper = (isOpen, title, onClose, children) => isOpen && (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl mb-8" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );

  const renderModalButtons = (onCancel, onSave) => (
    <div className="mt-5 flex justify-end gap-2">
      <button className="px-4 py-2 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-50" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }} onClick={onCancel}>Cancel</button>
      <button className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }} onClick={onSave}>Save</button>
    </div>
  );

  const renderSubTabHeader = (title, btnLabel, onAdd) => (
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-sm font-bold text-gray-800">{title}</h3>
      <button className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }} onClick={onAdd}><Plus className="w-3.5 h-3.5" /> {btnLabel}</button>
    </div>
  );

  const thClass = "px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider";
  const tdClass = "px-4 py-3 text-xs text-gray-600";
  const trStyle = { borderBottom: '1px solid rgba(99, 102, 241, 0.05)' };

  const renderNumberSeries = () => (
    <div>
      {renderSubTabHeader('Number Series Routes', 'Add Series', () => setShowNumberSeriesModal(true))}
      <div className="overflow-x-auto rounded-xl" style={cardStyle}>
        <table className="min-w-full">
          <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}><th className={thClass}>Prefix Start</th><th className={thClass}>Prefix End</th><th className={thClass}>Route</th><th className={thClass}>Priority</th><th className={thClass}>Status</th><th className={thClass}>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="px-6 py-12 text-center"><Hash className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400">Loading...</p></td></tr>
            : numberSeries.length === 0 ? <tr><td colSpan="6" className="px-6 py-12 text-center"><Hash className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No number series</p></td></tr>
            : numberSeries.map((s) => (
              <tr key={s.id} className="transition-colors hover:bg-indigo-50/30" style={trStyle}>
                <td className={`${tdClass} font-semibold text-gray-800`}>{s.prefix_start}</td>
                <td className={tdClass}>{s.prefix_end}</td>
                <td className={tdClass}>{s.route_name || `Route ${s.route_id}`}</td>
                <td className={tdClass}>{s.priority}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusBadgeStyle(s.is_active)}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-3">{renderActionButtons(s, 'number-series', () => { setEditingNumberSeries(s); setNumberSeriesForm(s); setShowNumberSeriesModal(true); })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renderModalWrapper(showNumberSeriesModal, editingNumberSeries ? 'Edit Number Series' : 'Add Number Series', () => { setShowNumberSeriesModal(false); setEditingNumberSeries(null); setNumberSeriesForm({ prefix_start: '', prefix_end: '', route_id: '', priority: 1, is_active: true }); }, (
        <div className="space-y-4">
          <div><label className={labelClass}>Prefix Start</label><input type="text" className={inputClass} style={inputBorder} value={numberSeriesForm.prefix_start} onChange={(e) => setNumberSeriesForm({ ...numberSeriesForm, prefix_start: e.target.value })} placeholder="91-98" /></div>
          <div><label className={labelClass}>Prefix End</label><input type="text" className={inputClass} style={inputBorder} value={numberSeriesForm.prefix_end} onChange={(e) => setNumberSeriesForm({ ...numberSeriesForm, prefix_end: e.target.value })} placeholder="91-98" /></div>
          <div><label className={labelClass}>Route</label><select className={inputClass} style={inputBorder} value={numberSeriesForm.route_id} onChange={(e) => setNumberSeriesForm({ ...numberSeriesForm, route_id: e.target.value })}><option value="">Select Route</option>{routes.map(r => <option key={r.id} value={r.id}>{r.route_name}</option>)}</select></div>
          <div><label className={labelClass}>Priority</label><input type="number" className={inputClass} style={inputBorder} value={numberSeriesForm.priority} onChange={(e) => setNumberSeriesForm({ ...numberSeriesForm, priority: parseInt(e.target.value) })} min="1" /></div>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={numberSeriesForm.is_active} onChange={(e) => setNumberSeriesForm({ ...numberSeriesForm, is_active: e.target.checked })} /><span className="text-xs font-medium text-gray-700">Active</span></label>
          {renderModalButtons(() => { setShowNumberSeriesModal(false); setEditingNumberSeries(null); setNumberSeriesForm({ prefix_start: '', prefix_end: '', route_id: '', priority: 1, is_active: true }); }, handleSaveNumberSeries)}
        </div>
      ))}
    </div>
  );

  const renderMobileNumbers = () => (
    <div>
      {renderSubTabHeader('Mobile Number Routes', 'Add Mobile', () => setShowMobileModal(true))}
      <div className="overflow-x-auto rounded-xl" style={cardStyle}>
        <table className="min-w-full">
          <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}><th className={thClass}>Mobile</th><th className={thClass}>Action</th><th className={thClass}>Route</th><th className={thClass}>Priority</th><th className={thClass}>Status</th><th className={thClass}>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="px-6 py-12 text-center"><Phone className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400">Loading...</p></td></tr>
            : mobileNumbers.length === 0 ? <tr><td colSpan="6" className="px-6 py-12 text-center"><Phone className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No mobile numbers</p></td></tr>
            : mobileNumbers.map((m) => (
              <tr key={m.id} className="transition-colors hover:bg-indigo-50/30" style={trStyle}>
                <td className={`${tdClass} font-semibold text-gray-800`}>{m.mobile_number}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold" style={m.action === 'ROUTE' ? { background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' } : { background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.15)' }}>{m.action}</span></td>
                <td className={tdClass}>{m.route_name || (m.action === 'ROUTE' ? `Route ${m.route_id}` : '-')}</td>
                <td className={tdClass}>{m.priority}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusBadgeStyle(m.is_active)}>{m.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-3">{renderActionButtons(m, 'mobile-numbers', () => { setEditingMobile(m); setMobileForm(m); setShowMobileModal(true); })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renderModalWrapper(showMobileModal, editingMobile ? 'Edit Mobile Number' : 'Add Mobile Number', () => { setShowMobileModal(false); setEditingMobile(null); setMobileForm({ mobile_number: '', action: 'ROUTE', route_id: '', priority: 1, is_active: true }); }, (
        <div className="space-y-4">
          <div><label className={labelClass}>Mobile Number</label><input type="text" className={inputClass} style={inputBorder} value={mobileForm.mobile_number} onChange={(e) => setMobileForm({ ...mobileForm, mobile_number: e.target.value })} placeholder="919876543210" /></div>
          <div><label className={labelClass}>Action</label><select className={inputClass} style={inputBorder} value={mobileForm.action} onChange={(e) => setMobileForm({ ...mobileForm, action: e.target.value })}><option value="ROUTE">Route</option><option value="BLOCK">Block</option></select></div>
          {mobileForm.action === 'ROUTE' && <div><label className={labelClass}>Route</label><select className={inputClass} style={inputBorder} value={mobileForm.route_id} onChange={(e) => setMobileForm({ ...mobileForm, route_id: e.target.value })}><option value="">Select Route</option>{routes.map(r => <option key={r.id} value={r.id}>{r.route_name}</option>)}</select></div>}
          <div><label className={labelClass}>Priority</label><input type="number" className={inputClass} style={inputBorder} value={mobileForm.priority} onChange={(e) => setMobileForm({ ...mobileForm, priority: parseInt(e.target.value) })} min="1" /></div>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={mobileForm.is_active} onChange={(e) => setMobileForm({ ...mobileForm, is_active: e.target.checked })} /><span className="text-xs font-medium text-gray-700">Active</span></label>
          {renderModalButtons(() => { setShowMobileModal(false); setEditingMobile(null); setMobileForm({ mobile_number: '', action: 'ROUTE', route_id: '', priority: 1, is_active: true }); }, handleSaveMobile)}
        </div>
      ))}
    </div>
  );

  const renderHeaders = () => (
    <div>
      {renderSubTabHeader('Header Routes', 'Add Header', () => setShowHeaderModal(true))}
      <div className="overflow-x-auto rounded-xl" style={cardStyle}>
        <table className="min-w-full">
          <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}><th className={thClass}>Header Name</th><th className={thClass}>Route</th><th className={thClass}>Priority</th><th className={thClass}>Status</th><th className={thClass}>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="5" className="px-6 py-12 text-center"><Tag className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400">Loading...</p></td></tr>
            : headers.length === 0 ? <tr><td colSpan="5" className="px-6 py-12 text-center"><Tag className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No headers</p></td></tr>
            : headers.map((h) => (
              <tr key={h.id} className="transition-colors hover:bg-indigo-50/30" style={trStyle}>
                <td className={`${tdClass} font-semibold text-gray-800`}>{h.header_name}</td>
                <td className={tdClass}>{h.route_name || `Route ${h.route_id}`}</td>
                <td className={tdClass}>{h.priority}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusBadgeStyle(h.is_active)}>{h.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-3">{renderActionButtons(h, 'headers', () => { setEditingHeader(h); setHeaderForm(h); setShowHeaderModal(true); })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renderModalWrapper(showHeaderModal, editingHeader ? 'Edit Header' : 'Add Header', () => { setShowHeaderModal(false); setEditingHeader(null); setHeaderForm({ header_name: '', route_id: '', priority: 1, is_active: true }); }, (
        <div className="space-y-4">
          <div><label className={labelClass}>Header Name</label><input type="text" className={inputClass} style={inputBorder} value={headerForm.header_name} onChange={(e) => setHeaderForm({ ...headerForm, header_name: e.target.value })} placeholder="WAY2OTP" /></div>
          <div><label className={labelClass}>Route</label><select className={inputClass} style={inputBorder} value={headerForm.route_id} onChange={(e) => setHeaderForm({ ...headerForm, route_id: e.target.value })}><option value="">Select Route</option>{routes.map(r => <option key={r.id} value={r.id}>{r.route_name}</option>)}</select></div>
          <div><label className={labelClass}>Priority</label><input type="number" className={inputClass} style={inputBorder} value={headerForm.priority} onChange={(e) => setHeaderForm({ ...headerForm, priority: parseInt(e.target.value) })} min="1" /></div>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={headerForm.is_active} onChange={(e) => setHeaderForm({ ...headerForm, is_active: e.target.checked })} /><span className="text-xs font-medium text-gray-700">Active</span></label>
          {renderModalButtons(() => { setShowHeaderModal(false); setEditingHeader(null); setHeaderForm({ header_name: '', route_id: '', priority: 1, is_active: true }); }, handleSaveHeader)}
        </div>
      ))}
    </div>
  );

  const renderKeywords = () => (
    <div>
      {renderSubTabHeader('Keyword Routes', 'Add Keyword', () => setShowKeywordModal(true))}
      <div className="overflow-x-auto rounded-xl" style={cardStyle}>
        <table className="min-w-full">
          <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}><th className={thClass}>Keyword</th><th className={thClass}>Route</th><th className={thClass}>Priority</th><th className={thClass}>Status</th><th className={thClass}>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="5" className="px-6 py-12 text-center"><FileText className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400">Loading...</p></td></tr>
            : keywords.length === 0 ? <tr><td colSpan="5" className="px-6 py-12 text-center"><FileText className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No keywords</p></td></tr>
            : keywords.map((k) => (
              <tr key={k.id} className="transition-colors hover:bg-indigo-50/30" style={trStyle}>
                <td className={`${tdClass} font-semibold text-gray-800`}>{k.keyword}</td>
                <td className={tdClass}>{k.route_name || `Route ${k.route_id}`}</td>
                <td className={tdClass}>{k.priority}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusBadgeStyle(k.is_active)}>{k.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-3">{renderActionButtons(k, 'keywords', () => { setEditingKeyword(k); setKeywordForm(k); setShowKeywordModal(true); })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renderModalWrapper(showKeywordModal, editingKeyword ? 'Edit Keyword' : 'Add Keyword', () => { setShowKeywordModal(false); setEditingKeyword(null); setKeywordForm({ keyword: '', route_id: '', priority: 1, is_active: true }); }, (
        <div className="space-y-4">
          <div><label className={labelClass}>Keyword</label><input type="text" className={inputClass} style={inputBorder} value={keywordForm.keyword} onChange={(e) => setKeywordForm({ ...keywordForm, keyword: e.target.value })} placeholder="OTP" /></div>
          <div><label className={labelClass}>Route</label><select className={inputClass} style={inputBorder} value={keywordForm.route_id} onChange={(e) => setKeywordForm({ ...keywordForm, route_id: e.target.value })}><option value="">Select Route</option>{routes.map(r => <option key={r.id} value={r.id}>{r.route_name}</option>)}</select></div>
          <div><label className={labelClass}>Priority</label><input type="number" className={inputClass} style={inputBorder} value={keywordForm.priority} onChange={(e) => setKeywordForm({ ...keywordForm, priority: parseInt(e.target.value) })} min="1" /></div>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={keywordForm.is_active} onChange={(e) => setKeywordForm({ ...keywordForm, is_active: e.target.checked })} /><span className="text-xs font-medium text-gray-700">Active</span></label>
          {renderModalButtons(() => { setShowKeywordModal(false); setEditingKeyword(null); setKeywordForm({ keyword: '', route_id: '', priority: 1, is_active: true }); }, handleSaveKeyword)}
        </div>
      ))}
    </div>
  );

  const getScopeStyle = (scope) => {
    if (scope === 'GLOBAL') return { background: 'rgba(124, 58, 237, 0.08)', color: '#7c3aed', border: '1px solid rgba(124, 58, 237, 0.15)' };
    if (scope === 'ENTITY') return { background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' };
    return { background: 'rgba(16, 185, 129, 0.08)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.15)' };
  };

  const renderReplacements = () => (
    <div>
      {renderSubTabHeader('Keyword Replacements', 'Add Replace', () => { setShowReplacementModal(true); setEntitySearchTerm(''); setPlanSearchTerm(''); setShowEntityDropdown(false); setShowPlanDropdown(false); })}
      <div className="overflow-x-auto rounded-xl" style={cardStyle}>
        <table className="min-w-full">
          <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}><th className={thClass}>Find</th><th className={thClass}>Replace</th><th className={thClass}>Scope</th><th className={thClass}>Status</th><th className={thClass}>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="5" className="px-6 py-12 text-center"><RefreshCw className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400">Loading...</p></td></tr>
            : replacements.length === 0 ? <tr><td colSpan="5" className="px-6 py-12 text-center"><RefreshCw className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No replacements</p></td></tr>
            : replacements.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-indigo-50/30" style={trStyle}>
                <td className={`${tdClass} font-semibold text-gray-800`}>{r.find_text}</td>
                <td className={tdClass}>{r.replace_text}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold" style={getScopeStyle(r.scope)}>{r.scope}</span></td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusBadgeStyle(r.is_active)}>{r.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-0.5">
                    <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50" title="Edit" onClick={() => {
                      setEditingReplacement(r); setReplacementForm(r); setShowReplacementModal(true);
                      if (r.scope === 'ENTITY' && r.entity_id) { const ent = entities.find(e => e.id === r.entity_id); if (ent) setEntitySearchTerm(ent.entity_name); }
                      if (r.scope === 'PLAN' && r.plan_id) { const pl = plans.find(p => p.id === r.plan_id); if (pl) setPlanSearchTerm(pl.plan_name); }
                    }}><Pencil className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /></button>
                    <button className={`w-7 h-7 rounded-lg flex items-center justify-center ${r.is_active ? 'hover:bg-amber-50' : 'hover:bg-green-50'}`} title="Toggle" onClick={() => handleToggleStatus('keyword-replacements', r.id)}>{r.is_active ? <Square className="w-3.5 h-3.5 text-amber-500" /> : <Play className="w-3.5 h-3.5 text-green-500" />}</button>
                    <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50" title="Delete" onClick={() => handleDelete('keyword-replacements', r.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renderModalWrapper(showReplacementModal, editingReplacement ? 'Edit Replacement' : 'Add Replacement', () => { setShowReplacementModal(false); setEditingReplacement(null); setReplacementForm({ find_text: '', replace_text: '', scope: 'GLOBAL', entity_id: '', plan_id: '', is_active: true }); setEntitySearchTerm(''); setPlanSearchTerm(''); }, (
        <div className="space-y-4">
          <div><label className={labelClass}>Find Text</label><input type="text" className={inputClass} style={inputBorder} value={replacementForm.find_text} onChange={(e) => setReplacementForm({ ...replacementForm, find_text: e.target.value })} placeholder="text to find" /></div>
          <div><label className={labelClass}>Replace Text</label><input type="text" className={inputClass} style={inputBorder} value={replacementForm.replace_text} onChange={(e) => setReplacementForm({ ...replacementForm, replace_text: e.target.value })} placeholder="replacement text" /></div>
          <div><label className={labelClass}>Scope</label><select className={inputClass} style={inputBorder} value={replacementForm.scope} onChange={(e) => { setReplacementForm({ ...replacementForm, scope: e.target.value, entity_id: '', plan_id: '' }); setEntitySearchTerm(''); setPlanSearchTerm(''); setShowEntityDropdown(false); setShowPlanDropdown(false); }}><option value="GLOBAL">Global</option><option value="ENTITY">Entity</option><option value="PLAN">Plan</option></select></div>
          {replacementForm.scope === 'ENTITY' && (
            <div><label className={labelClass}>Entity</label><div className="relative" ref={entityDropdownRef}><input type="text" className={inputClass} style={inputBorder} placeholder="Search entity..." value={entitySearchTerm || (entities.find(e => e.id === replacementForm.entity_id)?.entity_name || '')} onChange={(e) => { setEntitySearchTerm(e.target.value); setShowEntityDropdown(true); }} onFocus={() => setShowEntityDropdown(true)} />
              {showEntityDropdown && <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg max-h-48 overflow-auto" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}>{entities.filter(en => en.entity_name.toLowerCase().includes(entitySearchTerm.toLowerCase())).map(en => (<div key={en.id} className="px-3 py-2 text-xs cursor-pointer hover:bg-indigo-50" onClick={() => { setReplacementForm({ ...replacementForm, entity_id: en.id }); setEntitySearchTerm(en.entity_name); setShowEntityDropdown(false); }}>{en.entity_name}</div>))}</div>}
            </div></div>
          )}
          {replacementForm.scope === 'PLAN' && (
            <div><label className={labelClass}>Plan</label><div className="relative" ref={planDropdownRef}><input type="text" className={inputClass} style={inputBorder} placeholder="Search plan..." value={planSearchTerm || (plans.find(p => p.id === replacementForm.plan_id)?.plan_name || '')} onChange={(e) => { setPlanSearchTerm(e.target.value); setShowPlanDropdown(true); }} onFocus={() => setShowPlanDropdown(true)} />
              {showPlanDropdown && <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg max-h-48 overflow-auto" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}>{plans.filter(pl => pl.plan_name.toLowerCase().includes(planSearchTerm.toLowerCase())).map(pl => (<div key={pl.id} className="px-3 py-2 text-xs cursor-pointer hover:bg-indigo-50" onClick={() => { setReplacementForm({ ...replacementForm, plan_id: pl.id }); setPlanSearchTerm(pl.plan_name); setShowPlanDropdown(false); }}>{pl.plan_name}</div>))}</div>}
            </div></div>
          )}
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={replacementForm.is_active} onChange={(e) => setReplacementForm({ ...replacementForm, is_active: e.target.checked })} /><span className="text-xs font-medium text-gray-700">Active</span></label>
          {renderModalButtons(() => { setShowReplacementModal(false); setEditingReplacement(null); setReplacementForm({ find_text: '', replace_text: '', scope: 'GLOBAL', entity_id: '', plan_id: '', is_active: true }); setEntitySearchTerm(''); setPlanSearchTerm(''); }, handleSaveReplacement)}
        </div>
      ))}
    </div>
  );

  const renderRoutingPlans = () => (
    <div>
      {renderSubTabHeader('Routing Plans', 'Add Plan', () => setShowRoutingPlanModal(true))}
      <div className="overflow-x-auto rounded-xl" style={cardStyle}>
        <table className="min-w-full">
          <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}><th className={thClass}>Plan Name</th><th className={thClass}>Description</th><th className={thClass}>Priority</th><th className={thClass}>Status</th><th className={thClass}>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="5" className="px-6 py-12 text-center"><Server className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400">Loading...</p></td></tr>
            : routingPlans.length === 0 ? <tr><td colSpan="5" className="px-6 py-12 text-center"><Server className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No routing plans</p></td></tr>
            : routingPlans.map((p) => (
              <tr key={p.id} className="transition-colors hover:bg-indigo-50/30" style={trStyle}>
                <td className={`${tdClass} font-semibold text-gray-800`}>{p.plan_name}</td>
                <td className={tdClass}>{p.description || '-'}</td>
                <td className={tdClass}>{p.priority}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusBadgeStyle(p.is_active)}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-0.5">
                    <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50" title="Edit" onClick={() => { setEditingRoutingPlan(p); setRoutingPlanForm(p); setShowRoutingPlanModal(true); }}><Pencil className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /></button>
                    <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-blue-50" title="Test" onClick={() => handleTestRoutingPlan(p.id)}><Play className="w-3.5 h-3.5 text-blue-500" /></button>
                    <button className={`w-7 h-7 rounded-lg flex items-center justify-center ${p.is_active ? 'hover:bg-amber-50' : 'hover:bg-green-50'}`} title="Toggle" onClick={() => handleToggleStatus('routing-plans', p.id)}>{p.is_active ? <Square className="w-3.5 h-3.5 text-amber-500" /> : <Play className="w-3.5 h-3.5 text-green-500" />}</button>
                    <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50" title="Delete" onClick={() => handleDeleteRoutingPlan(p.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renderModalWrapper(showRoutingPlanModal, editingRoutingPlan ? 'Edit Routing Plan' : 'Add Routing Plan', () => { setShowRoutingPlanModal(false); setEditingRoutingPlan(null); setRoutingPlanForm({ plan_name: '', description: '', priority: 1, is_active: true }); }, (
        <div className="space-y-4">
          <div><label className={labelClass}>Plan Name</label><input type="text" className={inputClass} style={inputBorder} value={routingPlanForm.plan_name} onChange={(e) => setRoutingPlanForm({ ...routingPlanForm, plan_name: e.target.value })} placeholder="Enter plan name" /></div>
          <div><label className={labelClass}>Description</label><textarea className={`${inputClass} h-auto py-2`} style={inputBorder} rows="3" value={routingPlanForm.description} onChange={(e) => setRoutingPlanForm({ ...routingPlanForm, description: e.target.value })} placeholder="Enter description" /></div>
          <div><label className={labelClass}>Priority</label><input type="number" min="1" className={inputClass} style={inputBorder} value={routingPlanForm.priority} onChange={(e) => setRoutingPlanForm({ ...routingPlanForm, priority: parseInt(e.target.value) || 1 })} /></div>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={routingPlanForm.is_active} onChange={(e) => setRoutingPlanForm({ ...routingPlanForm, is_active: e.target.checked })} /><span className="text-xs font-medium text-gray-700">Active</span></label>
          {renderModalButtons(() => { setShowRoutingPlanModal(false); setEditingRoutingPlan(null); setRoutingPlanForm({ plan_name: '', description: '', priority: 1, is_active: true }); }, handleSaveRoutingPlan)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Network className="w-5 h-5 text-white" />
            </div>
            Advanced Routing
          </h1>
          <p className="text-sm text-gray-500 mt-1">Priority-based SMS routing rules</p>
        </div>
        <button
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm"
          style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)', color: '#d97706' }}
          onClick={() => window.location.reload()}
        >
          <RefreshCw className="w-3.5 h-3.5" /> Reload
        </button>
      </div>

      {/* Statistics Cards */}
      {renderStatCards()}

      {/* Tabs */}
      <div className="bg-white rounded-xl" style={cardStyle}>
        <div className="flex flex-wrap gap-0 px-2 pt-2 overflow-x-auto" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          {[
            { id: 'routes', name: 'Routes', icon: Server, count: routes.length },
            { id: 'evaluation', name: 'Evaluate', icon: Play },
            { id: 'number-series', name: 'Series', icon: Hash, count: numberSeries.length },
            { id: 'mobile-numbers', name: 'Mobile', icon: Phone, count: mobileNumbers.length },
            { id: 'headers', name: 'Headers', icon: Tag, count: headers.length },
            { id: 'keywords', name: 'Keywords', icon: FileText, count: keywords.length },
            { id: 'replacements', name: 'Replace', icon: RefreshCw, count: replacements.length },
            { id: 'routing-plans', name: 'Plans', icon: Server, count: routingPlans.length },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold rounded-t-lg transition-colors whitespace-nowrap ${activeTab === tab.id ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
                style={activeTab === tab.id ? { background: 'rgba(99, 102, 241, 0.06)', borderBottom: '2px solid #4f46e5' } : {}}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.name}
                {tab.count !== undefined && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold" style={{ background: activeTab === tab.id ? 'rgba(99, 102, 241, 0.1)' : 'rgba(107, 114, 128, 0.08)', color: activeTab === tab.id ? '#4f46e5' : '#9ca3af' }}>{tab.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-5">
          {activeTab === 'routes' && renderRoutes()}
          {activeTab === 'evaluation' && renderRouteEvaluation()}
          {activeTab === 'number-series' && renderNumberSeries()}
          {activeTab === 'mobile-numbers' && renderMobileNumbers()}
          {activeTab === 'headers' && renderHeaders()}
          {activeTab === 'keywords' && renderKeywords()}
          {activeTab === 'replacements' && renderReplacements()}
          {activeTab === 'routing-plans' && renderRoutingPlans()}
        </div>
      </div>
    </div>
  );
};

export default AdvancedRoutingManagement;